import React from 'react';
import {
  describeType,
  type UpstreamFieldOption,
  type UpstreamSourceGroup,
} from './variable-picker-data';
import { VariableFieldRow } from './variable-field-row';

/**
 * One headed run of fields in a variable picker.
 *
 * The unit both surfaces render, so a field sits under the same heading in the
 * `/` menu, in the bolt popover, and in the dataset trigger's field box. Which
 * sections exist is the caller's to say — see {@link sourceSections} for the
 * workflow ones and {@link datasetColumnSections} for a trigger's.
 */
export type VariablePickerSection = {
  readonly heading: string;
  readonly fields: readonly UpstreamFieldOption[];
};

/**
 * One section per upstream source, nearest first, each headed by that source's
 * name.
 *
 * The source moves from a chip repeated on every row to a single heading over
 * its own fields — frozen to the top of the list as you scroll (see the picker
 * surfaces), so it stays visible without costing every row up to 45% of its
 * width. A field whose name is data rather than an identifier — a product title
 * keying a quantities map — then gets the whole row and reads in full instead
 * of truncating however wide the popover is.
 *
 * Replaces the "Previous step" / "Earlier outputs" depth split: ordering the
 * source headings nearest-first keeps the same "closest thing is at the top"
 * reading, while naming each source outright says more than which band it fell
 * in.
 */
export function sourceSections(
  sources: readonly UpstreamSourceGroup[]
): VariablePickerSection[] {
  return [...sources]
    .filter((group) => group.fields.length > 0)
    .sort((a, b) => a.sourceDepth - b.sourceDepth)
    .map((group) => ({ heading: group.label, fields: group.fields }));
}

/**
 * Every field under one heading, for a picker whose fields are not upstream
 * steps at all.
 *
 * The per-source headings {@link sourceSections} gives each name a step the
 * reader can go and look at, which is a lie for a picker offering the fields of
 * one already-stored value — a bucket's accumulated item. Those came from
 * wherever `accumulate` pointed, and are not a step the reader can go and look
 * at, so they sit under one caller-named heading instead.
 */
export function singleVariableSection(
  heading: string,
  sources: readonly UpstreamSourceGroup[]
): VariablePickerSection[] {
  const fields = sources.flatMap((group) => group.fields);
  return fields.length === 0 ? [] : [{ heading, fields }];
}

/**
 * A dataset's own columns as pickable fields, for a surface with no upstream
 * graph at all.
 *
 * A dataset trigger's condition tests the row that changed, so its fields are
 * that dataset's columns and its refs are bare JSON Pointers (`/email`) — the
 * form the evaluator reads. The picker needs no special case for them: a bare
 * pointer parses to "no source, this pointer", which is exactly what a ref
 * against the row means.
 *
 * It had a `<Select>` of column names before — no search, no value hints, and
 * nothing in common with the two pickers the workflow builder uses three
 * screens away.
 */
export function datasetColumnSections(params: {
  readonly columns: readonly string[];
  readonly heading?: string;
}): VariablePickerSection[] {
  if (params.columns.length === 0) {
    return [];
  }
  return [
    {
      heading: params.heading ?? 'Columns',
      fields: params.columns.map((column) => ({
        ref: `/${column}`,
        sourceName: '',
        label: '',
        leaf: column,
        depth: 0,
        sourceDepth: 1,
        // No sample row to read a type off, so leave it unknown: `describeType`
        // renders `undefined` as `any` rather than guessing a column's type from
        // a value it does not have.
        value: undefined,
        searchText: column,
      })),
    },
  ];
}

/** What a picker says when the search matches nothing. */
export const VARIABLE_PICKER_EMPTY_TEXT = 'No matching fields';

/**
 * The innards of one selectable row, identical in both surfaces.
 *
 * Deliberately not the clickable element: the `/` menu drives selection from
 * the editor's suggestion plugin and the popover drives it from cmdk, so each
 * owns its own wrapper. Everything inside the wrapper is this.
 */
export const VariablePickerRow: React.FC<{
  readonly field: UpstreamFieldOption;
}> = ({ field }) => {
  const described = describeType(field.value);
  // No source here any more: it's the group's (sticky) heading, so the whole
  // row is the field name and its type hint.
  return (
    <VariableFieldRow
      leaf={field.leaf}
      depth={field.depth}
      hint={described.text}
      hintMono={described.mono}
    />
  );
};

/**
 * Whether a field answers to a search query.
 *
 * One rule for both surfaces. The two matched differently before: the popover
 * searched cmdk's row value, and the `/` menu also matched a row's *hint* — so
 * typing `won` there surfaced every field whose sample value happened to be
 * `won`, which is not a field name anyone was looking for.
 */
export function variableFieldMatches(
  field: UpstreamFieldOption,
  query: string
): boolean {
  const normalized = query.trim().toLowerCase();
  return (
    normalized === '' || field.searchText.toLowerCase().includes(normalized)
  );
}

/**
 * The width both surfaces render at, so one does not reflow into the other.
 *
 * 320px could not hold a field name, a value hint and the step's name at once:
 * a run of outputs sharing a prefix came out as four rows of `KEY_CONTA…`, with
 * the part that told them apart cut off. The source then moved out of the row
 * and into a per-source heading ({@link sourceSections}), freeing the row for
 * the field name and its value hint; 480px then gives names that are data, not
 * identifiers — a product-quantities map keyed on `Black You Got This T-Shirt
 * (86812993)` — the room to read in full rather than middle-truncating.
 *
 * Both surfaces portal out and collision-position against the viewport (the
 * bolt popover via Radix, the `/` menu via floating-ui), so the wider box
 * shifts to stay on screen near the panel's right edge rather than running off
 * it.
 */
export const VARIABLE_PICKER_WIDTH = 'w-[480px]';
